import { useEffect } from 'react'
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import StripeConnectPaintedSurface from '@/components/stripe-connect/StripeConnectPaintedSurface'
import StripeConnectSection from '@/components/stripe-connect/StripeConnectSection'
import { STRIPE_CONNECT_ONBOARDING_SURFACE } from '@/components/stripe-connect/stripeConnectSurfaceContent'
import {
  STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
  useStripeConnectManager,
} from '@/components/stripe-connect/useStripeConnectManager'
import { AlertTriangle, RefreshCw } from 'lucide-react'

export default function StripeConnectOnboardingManager({
  organizationId,
  accountContext = 'current',
  onExit,
  onSessionConflict,
  onReadyChange,
  title = 'Resume Stripe setup',
  description = 'Complete the Stripe steps required to accept payments and payouts.',
}) {
  const {
    connectInstance,
    isEmbeddedSurfaceHydrating,
    isLoading,
    loadErrorMessage,
    handleLoadError,
    handleLoaderStart,
    handleSurfacePainted,
    renderAttemptKey,
    retryLoad,
  } = useStripeConnectManager({
    organizationId,
    accountContext,
    onSessionConflict,
    maxHydrationMs: STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
    surface: 'onboarding',
    toastTitle: 'Unable to load Stripe onboarding',
  })
  const showPlaceholder = isLoading || isEmbeddedSurfaceHydrating
  const isRenderable = Boolean(loadErrorMessage) || (Boolean(connectInstance) && !showPlaceholder)

  useEffect(() => {
    onReadyChange?.(isRenderable)
  }, [isRenderable, onReadyChange])

  if (loadErrorMessage) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Stripe setup is unavailable right now</AlertTitle>
        <AlertDescription>
          <p>{loadErrorMessage}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={retryLoad}>
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <StripeConnectSection title={title} description={description}>
      <StripeConnectPaintedSurface
        minHeightClassName={STRIPE_CONNECT_ONBOARDING_SURFACE.minHeightClassName}
        onLoaderStart={handleLoaderStart}
        onSurfacePainted={handleSurfacePainted}
        renderAttemptKey={renderAttemptKey}
        showPlaceholder={showPlaceholder}
      >
        {connectInstance
          ? ({ handleLoaderStart: handlePaintAwareLoaderStart }) => (
              <ConnectComponentsProvider connectInstance={connectInstance}>
                <ConnectAccountOnboarding
                  key={renderAttemptKey}
                  collectionOptions={{
                    fields: 'eventually_due',
                    futureRequirements: 'include',
                  }}
                  onExit={onExit ?? (() => {})}
                  onLoadError={handleLoadError}
                  onLoaderStart={handlePaintAwareLoaderStart}
                />
              </ConnectComponentsProvider>
            )
          : null}
      </StripeConnectPaintedSurface>
    </StripeConnectSection>
  )
}
